import {
  type DataGridPremiumProps,
  GRID_TREE_DATA_GROUPING_FIELD,
  type GridCallbackDetails,
  type GridFilterModel,
  type GridGroupNode,
  type GridPaginationModel,
  type GridSortModel,
  type GridValidRowModel,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { useDataRouterContext } from "src/lib/react-router/useDataRouterContext.ts";
import { useContentLocale } from "src/auth/hooks/useContentLocale.tsx";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  type UseQueryOptions,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  mapGridStateToPersistedState,
  type PersistedGridState,
} from "src/system/useResourceList/utils/mapGridStateToPersistedState.tsx";
import { useUrlState } from "@exonn/use-url-state";
import { deepEq, download, removeItem, unique } from "@exonn/utils";

import type { GridEventListener } from "@mui/x-data-grid";
import type { DataGridProps } from "src/components/DataGrid/DataGrid.tsx";

import { useList } from "src/system/useList.tsx";
import {
  useDataProvider,
  useResourceQueries,
  type ValidGetListResponse,
} from "@exonn/base";
import { useResourceStorageKeys } from "src/system/useResourceStorageKeys.tsx";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { useSnackbar } from "src/notifications/SnackbarProvider.ts";
import { logger } from "src/logger";
import { useUser } from "src/auth/auth.ts";
import { useUserDataState } from "src/user-data/useUserDataState.ts";
import {
  DataGridExpandedRowsSchema,
  DataGridPersistedStateSchema,
} from "src/user-data/schemas.ts";
import type { DataGridExpandedRowsPersistedState } from "src/user-data/types.ts";
import { useGlobalSettings } from "src/hooks/use-global-settings.ts";

import { useProductGridProps } from "src/system/useResourceList/useProductGridProps.tsx";
import { useClientConfig } from "src/client-config/useClientConfig.ts";
import { resourceListConfigQuery } from "src/system/resourceListConfigQuery.ts";
import { prepareFilterModel } from "src/system/useResourceList/utils/prepareFilterModel.tsx";
import { mergeFilterModels } from "src/components/DataGrid/mergeFilterModels.ts";
import { getResourceI18nNamespace } from "src/system/getResourceI18nNamespace.ts";
import { useErpTranslation } from "src/system/useErpTranslation.ts";
import { createMapRowsResult } from "src/system/useResourceList/utils/createMapRowResult.ts";
import { convertGridFilterModelToUrlFilterModel } from "src/system/useResourceList/utils/convertGridFilterModelToUrlFilterModel.ts";
import { convertGridSortModelToUrlSortModel } from "src/system/useResourceList/utils/convertGridSortModelToUrlSortModel.ts";
import { convertUrlStateToGridState } from "src/system/useResourceList/utils/convertUrlStateToGridState.ts";
import {
  ResourceListSearchParams,
  type UrlState,
} from "src/system/useResourceList/useResourceList.types.ts";
import { filterModelWithoutIds } from "src/system/useResourceList/utils/filterModelWithoutIds.ts";
import { hideIdColumnInInitialState } from "src/system/useResourceList/utils/hideIdColumnInInitialState.ts";

import {
  DEVELOPER_COLUMNS,
  urlStateValidationSchema,
} from "src/system/useResourceList/useResourceList.constants.ts";
import { prepareSortModel } from "src/system/useResourceList/utils/prepareSortModel.ts";
import { defaultListSort } from "src/system/constants.ts";
import { useStorageState } from "src/hooks/use-storage-state.ts";
import { memoryStorageDriver } from "src/storage/memoryStorage.driver.ts";
import type { ResourceListApi } from "src/system/ResourceListContext.tsx";
import { useExonnApi } from "src/exonn-api/exonn-api.tsx";
import dayjs from "dayjs";
import type { ListActions } from "src/system/module.types.ts";
import { loadErpTranslations } from "src/system/loadErpTranslations.ts";
import {
  buildCsv,
  getCsvColumns,
} from "src/system/useResourceList/utils/listCsv.ts";

/**
 * Страница выгрузки — потолок json-api-paginate на бэке (max_results).
 */
const CSV_PAGE_SIZE = 10_000;

/**
 * Больше браузер собирает в память ощутимо долго.
 */
const CSV_ROW_LIMIT = 50_000;

class CsvRowLimitError extends Error {
  constructor(public readonly total: number) {
    super(`CSV export is limited to ${CSV_ROW_LIMIT} rows, ${total} matched`);
  }
}

export type UseResourceListProps<
  Row extends GridValidRowModel = GridValidRowModel,
> = {
  resource: string;
  filter?: GridFilterModel;
  filter2?: GridFilterModel;
  include?: string[];
  disableStatePersistenceToUrl?: boolean;
  actions?: ListActions<Row>;
  expandRowOnRowClick?: boolean;
  withoutDescendants?: boolean;
  queryOptions?: Omit<
    UseQueryOptions<
      ValidGetListResponse<Row>,
      any,
      ValidGetListResponse<Row>,
      any
    >,
    "queryFn" | "queryKey"
  >;
  saveToDatabase?: boolean;
} & Partial<DataGridProps<Row>>;

export const useResourceList = <
  Row extends GridValidRowModel = GridValidRowModel,
>(
  props: UseResourceListProps<Row>,
) => {
  const {
    disableStatePersistenceToUrl = false,
    loading: loadingProp,
    withoutDescendants = false,
    expandRowOnRowClick = false,
    resource,
    actions,
    include: includeProp,
    filter: staticFilter,
    filter2: staticFilter2,
    saveToDatabase = true,
    onToolbarRefresh: onToolbarRefreshProp,
    apiRef: apiRefProp,
    queryOptions,
    onRowClick,
    ...restProps
  } = props;
  const { router } = useDataRouterContext();
  const pendingUrlState = useRef<UrlState>();
  const storageKeys = useResourceStorageKeys();
  const exonnApi = useExonnApi();
  const { getList } = useDataProvider();
  const [refreshIndex, setRefreshIndex] = useStorageState(
    storageKeys.list({
      resource,
      state: "refreshIndex",
    }),
    0,
    memoryStorageDriver,
    {
      disablePersistence: disableStatePersistenceToUrl,
    },
  );

  const { data: user } = useUser();
  const snackbar = useSnackbar();
  const { data: globalSettings } = useGlobalSettings();

  const { t } = useErpTranslation({
    resource: resource,
    type: "list",
  });

  const clientConfig = useClientConfig();
  const { data: listConfig, refetch: refetchListConfig } = useSuspenseQuery({
    ...resourceListConfigQuery({
      resource: resource,
      clientConfig,
    }),
  });

  const {
    toolbarStartAdditionalElements: toolbarStartAdditionalElementsProp = [],
    toolbarEndAdditionalElements: toolbarEndAdditionalElementsProp = [],
    loadAggregateData = false,
  } = actions || {};

  const {
    getColumns,
    mapResourceToRow,
    includes: includesFromConfig,
    disableInitialDataLoading = false,
  } = listConfig;

  const queries = useResourceQueries();

  const contentLocale = useContentLocale()!;

  const localApiRef = useGridApiRef();
  const apiRef = apiRefProp || localApiRef;

  const {
    setValue: setUserDataExpandedRowsState,
    clear: clearExpandedRowsState,
    getValue: getUserExpandedRowsState,
  } = useUserDataState<DataGridExpandedRowsPersistedState>({
    user: user!,
    validationSchema: DataGridExpandedRowsSchema,
    key: storageKeys.list({
      resource,
      state: "expandedRows",
    }),
    disableSavingToDatabase: true,
  });

  const { value: userDataState, setValue: setUserDataState } =
    useUserDataState<PersistedGridState>({
      user: user!,
      key: storageKeys.list({
        resource,
        state: "state",
      }),
      validationSchema: DataGridPersistedStateSchema,
      disableSavingToDatabase: !saveToDatabase,
    });

  const initialState = hideIdColumnInInitialState(userDataState);
  const defaultSort = defaultListSort[resource] || {};

  const defaultSortModel: GridSortModel = [];
  const defaultPaginationModel = {
    page: 0,
    pageSize: 0,
  };
  const defaultFilterModel = {
    items: [],
  };
  Object.entries(defaultSort).forEach(([field, dir]) => {
    if (dir) {
      defaultSortModel.push({
        field: field,
        sort: dir,
      });
    }
  });

  const [gridUrlState, setGridUrlState] = useUrlState<UrlState>(
    router,
    {
      [ResourceListSearchParams.FilterModel]: {},
      [ResourceListSearchParams.PageSize]: defaultPaginationModel.pageSize,
      [ResourceListSearchParams.Page]: defaultPaginationModel.page,
      [ResourceListSearchParams.SortModel]:
        convertGridSortModelToUrlSortModel(defaultSortModel),
    },
    {
      disablePersistence: disableStatePersistenceToUrl,
      validationSchema: urlStateValidationSchema,
    },
  );

  const {
    filterModel: appliedFilterModel,
    paginationModel,
    sortModel,
    notAppliedFilterModel,
  } = convertUrlStateToGridState(gridUrlState);

  const [dataLoadingEnabled, setDataLoadingEnabled] = useStorageState(
    storageKeys.list({
      resource,
      state: "dataLoadingEnabled",
    }),
    !disableInitialDataLoading || appliedFilterModel.items.length > 0,
    memoryStorageDriver,
    {
      disablePersistence: disableStatePersistenceToUrl,
    },
  );

  const setFilterModel = (model: GridFilterModel) => {
    const modelWithoutIds = filterModelWithoutIds(model);

    if (deepEq(notAppliedFilterModel, model)) {
      return;
    }

    const nextUrlState = {
      [ResourceListSearchParams.NotAppliedFilterModel]:
        convertGridFilterModelToUrlFilterModel(modelWithoutIds),
    };
    pendingUrlState.current = {
      ...pendingUrlState.current,
      ...nextUrlState,
    };

    setGridUrlState({
      ...pendingUrlState.current,
    });
    setTimeout(() => {
      pendingUrlState.current = {};
    });
  };

  const setSortModel = (model: GridSortModel) => {
    clearExpandedRowsState();

    let finalModel = model;

    const saveDefaultSortModel = [];
    defaultSortModel.forEach((item) => {
      const column = columns.find((column) => column.field === item.field);
      if (!column) {
        logger.warn(
          `There is no column for [${item.field} ${item.sort}] sort]`,
        );
      } else {
        saveDefaultSortModel.push(item);
      }
    });

    if (saveDefaultSortModel.length !== 0 && model.length === 0) {
      finalModel = [
        {
          ...sortModel[0],
          sort: sortModel[0].sort === "asc" ? "desc" : "asc",
        },
      ];
    }

    if (model.length > 0) {
      finalModel = model.map((item) => {
        const column = columns.find((column) => column.field === item.field);
        if (column?.type === "dateRange" && item.sort === "asc") {
          return { ...item, sort: "desc" };
        }
        return item;
      });
    }

    setGridUrlState(
      {
        [ResourceListSearchParams.SortModel]:
          convertGridSortModelToUrlSortModel(finalModel),
      },
      {
        onFinish: () => {
          setRefreshIndex((count) => count + 1);
          setDataLoadingEnabled(true);
        },
      },
    );
  };

  const setAppliedFilterModel = (appliedFilterModel: GridFilterModel) => {
    clearExpandedRowsState();

    const nextUrlState = {
      [ResourceListSearchParams.Page]: 0,
      [ResourceListSearchParams.FilterModel]:
        convertGridFilterModelToUrlFilterModel(appliedFilterModel),
      [ResourceListSearchParams.FilterModelLogicOperator]:
        appliedFilterModel.logicOperator,
    };
    pendingUrlState.current = {
      ...pendingUrlState.current,
      ...nextUrlState,
    };

    setGridUrlState({
      ...pendingUrlState.current,
    });
    setTimeout(() => {
      pendingUrlState.current = {};
      setRefreshIndex((count) => count + 1);
      setDataLoadingEnabled(true);
    });
  };

  const setPaginationModel = (
    model: GridPaginationModel,
    details: GridCallbackDetails,
  ) => {
    if (details.reason !== "autoPageSize") {
      clearExpandedRowsState();
    }

    const nextPage = Math.max(0, model.page);
    const nextPageSize = Math.max(model.pageSize, 0);
    const prevPage = gridUrlState[ResourceListSearchParams.Page];
    const prevPageSize = gridUrlState[ResourceListSearchParams.PageSize];

    if (nextPage === prevPage && prevPageSize === nextPageSize) {
      return;
    }

    if (nextPageSize === 0) {
      return;
    }

    setGridUrlState(
      {
        [ResourceListSearchParams.Page]: Math.max(0, model.page),
        [ResourceListSearchParams.PageSize]: Math.max(model.pageSize, 0),
      },
      {
        onFinish: () => {
          setRefreshIndex((count) => count + 1);
        },
      },
    );
  };

  const shouldLoadList = () => {
    if (!paginationModel.pageSize) {
      return false;
    }
    return dataLoadingEnabled;
  };

  const combinedInclude = unique([
    ...(includeProp || []),
    ...(includesFromConfig || []),
  ]);

  const filter = prepareFilterModel(
    {
      ...appliedFilterModel,
      items: [...appliedFilterModel.items, ...(staticFilter?.items || [])],
    },
    getColumns({
      t,
      data: undefined,
      clientConfig,
      globalSettings,
    }),
  );

  const sort = prepareSortModel(
    sortModel,
    getColumns({
      t,
      data: undefined,
      clientConfig,
      globalSettings,
    }),
  );

  const { query: queryResult, queryKey } = useList<Row>({
    resource: props.resource,
    locale: contentLocale,
    refreshId: refreshIndex,
    include: combinedInclude,
    withAggregation: true,
    filter2: staticFilter2,

    filter: filter,
    sort: sort,
    page: {
      number: paginationModel.page + 1,
      size: paginationModel.pageSize,
    },
    queryOptions: {
      structuralSharing: false,
      enabled: shouldLoadList(),
      staleTime: disableStatePersistenceToUrl ? 0 : Infinity, // set to 'Infinity' to prevent query from automatically refetching on mount
      gcTime: disableStatePersistenceToUrl ? 0 : Infinity,
      refetchOnMount: true,
      placeholderData: keepPreviousData,
      ...queryOptions,
    },
  });

  const aggregateDataQuery = useQuery({
    queryKey: queries.list({
      resource,
      filter,
      isAggregation: true,
      meta: {
        refreshId: refreshIndex,
      },
    }),
    queryFn: async ({ signal }) => {
      const response = await exonnApi.client.get(`/${resource}/aggregates`, {
        signal,
        params: {
          filter,
        },
      });
      return response.data;
    },
    enabled: loadAggregateData && shouldLoadList(),
  });

  /**
   * Тот же запрос, что у списка: фильтры, сортировка, связи, язык контента.
   */
  const fetchCsvPage = async (pageNumber: number) =>
    (await getList({
      resource,
      filter,
      sort,
      page: { number: pageNumber, size: CSV_PAGE_SIZE },
      meta: {
        include: combinedInclude,
        filter2: staticFilter2,
        locale: contentLocale,
      },
    })) as ValidGetListResponse<Row>;

  const { mutate: exportCsv, isPending: isCsvExportPending } = useMutation({
    mutationFn: async () => {
      const firstPage = await fetchCsvPage(1);
      const total = firstPage.meta.total;

      if (total > CSV_ROW_LIMIT) {
        throw new CsvRowLimitError(total);
      }

      const pages = [firstPage];
      for (
        let pageNumber = 2;
        pageNumber <= Math.ceil(total / CSV_PAGE_SIZE);
        pageNumber++
      ) {
        pages.push(await fetchCsvPage(pageNumber));
      }

      const csvRows = pages.flatMap((page) => {
        const { rows: pageRows, error } = createMapRowsResult({
          mapResourceToRow,
          data: page,
        });
        if (error) {
          throw error;
        }
        return pageRows;
      });

      const numberFields = new Set(
        columns
          .filter((column) => column.type === "number")
          .map((column) => column.field),
      );

      return buildCsv({
        rows: csvRows,
        columns: getCsvColumns(
          apiRef.current.getVisibleColumns(),
          numberFields,
        ),
        apiRef,
        labels: {
          yes: t("csv-export.yes", { ns: TranslationNamespaceEnum.Common }),
          no: t("csv-export.no", { ns: TranslationNamespaceEnum.Common }),
        },
      });
    },
  });

  const { data: listData, isFetching, error: listError } = queryResult;

  const columns = useMemo(() => {
    const cols = getColumns({
      t,
      data: listData,
      globalSettings,
      clientConfig,
      staticFilter,
    }).map(({ headerName, field, ...restColumn }) => ({
      headerName: headerName ?? t(`table.columns.${field}`),
      field,
      ...restColumn,
    }));

    return cols.filter((column) => !DEVELOPER_COLUMNS.includes(column.field));
  }, [getColumns, staticFilter, listData, t, globalSettings, clientConfig]);

  const handleRecoverFromError = () => {
    apiRef.current.setSortModel(defaultSortModel);
    apiRef.current.setPaginationModel(defaultPaginationModel);
    apiRef.current.setFilterModel(defaultFilterModel);
    setAppliedFilterModel(defaultFilterModel);

    setRefreshIndex((count) => count + 1);
  };

  const { rows, error: mapRowsError } = createMapRowsResult({
    mapResourceToRow: mapResourceToRow,
    data: listData,
  });

  const [showedErrors] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (mapRowsError && mapRowsError instanceof Error) {
      if (showedErrors.has(mapRowsError.message)) {
        return;
      }
      snackbar.error({
        flash: false,
        message: t("notifications.list-mapping-failed", {
          ns: TranslationNamespaceEnum.Common,
          error: mapRowsError.message,
        }),
      });
      showedErrors.add(mapRowsError.message);
      logger.error(mapRowsError);
    }
  }, [mapRowsError, snackbar, showedErrors, t]);

  const hideToolbarPagination = !dataLoadingEnabled;

  const loading = isFetching || loadingProp;

  const handleToolbarRefresh = () => {
    enableDataLoading();
    clearExpandedRowsState();
    setRefreshIndex((count) => count + 1);
    const mergedModel = mergeFilterModels(
      notAppliedFilterModel,
      appliedFilterModel,
    );
    setAppliedFilterModel(mergedModel);
    setFilterModel(defaultFilterModel);
    setSortModel(defaultSortModel);

    onToolbarRefreshProp?.();
    if (import.meta.env.DEV) {
      refetchListConfig();
      loadErpTranslations({
        type: "list",
        resource,
      });
    }
  };

  const enableDataLoading = () => {
    setDataLoadingEnabled(true);
  };

  const isGroupExpandedByDefaultInternal = useCallback(
    (node: GridGroupNode) => {
      const stateFromStorage = getUserExpandedRowsState() || [];

      return stateFromStorage.includes(node.id);
    },
    [getUserExpandedRowsState],
  );

  const handleStateChange: DataGridPremiumProps<Row>["onStateChange"] = (
    state,
  ) => {
    const orderedFields = unique([
      // make sure grouping column always at the beginning
      // fixes https://link340.lcube-server.de/exonn-erp2/erp2-frontend/-/issues/88
      ...(props.treeData ? [GRID_TREE_DATA_GROUPING_FIELD] : []),
      ...(state?.columns?.orderedFields || []),
    ]);

    const finalState = {
      ...state,
      columns: {
        ...state.columns,
        orderedFields,
      },
    };
    setUserDataState(mapGridStateToPersistedState(finalState));
  };

  useEffect(() => {
    const onRowExpansionChange: GridEventListener<"rowExpansionChange"> = (
      params,
    ) => {
      const { childrenExpanded, id } = params;
      const prevExpanded = getUserExpandedRowsState() || [];

      const nextExpanded = childrenExpanded
        ? [...prevExpanded, id]
        : removeItem(prevExpanded, id);

      setUserDataExpandedRowsState(nextExpanded);
    };
    return apiRef.current?.subscribeEvent?.(
      "rowExpansionChange",
      onRowExpansionChange,
    );
  }, [apiRef, getUserExpandedRowsState, setUserDataExpandedRowsState]);

  const rowCount = listData ? listData.meta.total : 0;

  const productGridProps = useProductGridProps({
    resource,
    locale: contentLocale,
    withoutDescendants,
    apiRef,
    expandRowOnRowClick,
    onRowClick,
    mapResourceToRow: mapResourceToRow!,
    includes: combinedInclude,
    listQueryKey: queryKey,
  });

  const toolbarStartAdditionalElements = [
    ...(typeof toolbarStartAdditionalElementsProp === "function"
      ? toolbarStartAdditionalElementsProp({
          data: queryResult.data,
        })
      : toolbarStartAdditionalElementsProp),
  ];

  const toolbarEndAdditionalElements =
    typeof toolbarEndAdditionalElementsProp === "function"
      ? toolbarEndAdditionalElementsProp({
          data: queryResult.data,
          aggregateData: aggregateDataQuery.data,
          isAggregateDataFetching: aggregateDataQuery.isFetching,
        })
      : toolbarEndAdditionalElementsProp;

  const noRowsOverlayTitle = t(
    `noRows`,
    t("noRows", {
      ns: TranslationNamespaceEnum.DataGridComponent,
    }),
    {
      ns: getResourceI18nNamespace(resource),
    },
  );

  const handleExportToCsv = () => {
    exportCsv(undefined, {
      onSuccess: (csv) => {
        download(
          new Blob([csv], { type: "text/csv;charset=utf-8" }),
          t("csvFileName", {
            defaultValue: `${resource}_{{date}}`,
            date: dayjs().format("DD.MM.YYYY_HH-mm"),
          }) + ".csv",
        );
      },
      onError: (error) => {
        logger.error(error);
        snackbar.error({
          message:
            error instanceof CsvRowLimitError
              ? t("csv-export.too-many-rows", {
                  ns: TranslationNamespaceEnum.Common,
                  limit: CSV_ROW_LIMIT.toLocaleString("de-DE"),
                  found: error.total.toLocaleString("de-DE"),
                })
              : t("csv-export.failed", {
                  ns: TranslationNamespaceEnum.Common,
                }),
        });
      },
    });
  };

  const dataGridProps: DataGridProps<Row> = {
    rowCount,
    sortingMode: "server",
    paginationMode: "server",
    filterMode: "server",
    rows: listError ? [] : rows,
    onRowClick,
    appliedFilterModel: appliedFilterModel,
    onAppliedFilterModelChange: setAppliedFilterModel,
    disableVirtualization: true,
    columns,
    loading,
    initialState,
    showNoInitialDataLoadedOverlay: !dataLoadingEnabled,
    onLoadInitialData: enableDataLoading,
    onToolbarRefresh: handleToolbarRefresh,
    onStateChange: handleStateChange,
    toolbarStartAdditionalElements,
    toolbarEndAdditionalElements,
    isGroupExpandedByDefault: isGroupExpandedByDefaultInternal,
    filterModel: notAppliedFilterModel,
    sortModel: sortModel,
    noRowsOverlayTitle: noRowsOverlayTitle,
    disableMultipleRowSelection: true,
    paginationModel: paginationModel,
    onPaginationModelChange: setPaginationModel,
    onSortModelChange: setSortModel,
    onFilterModelChange: setFilterModel,
    onRecoverFromError: handleRecoverFromError,
    error: listError,
    hideToolbarPagination,
    createToolbarNewRecordText: t(`new`, {
      ns: getResourceI18nNamespace(resource),
    }),

    onToolbarExportCsv: handleExportToCsv,
    toolbarCsvExportPending: isCsvExportPending,

    apiRef,
    autoPageSize: true,
    ...productGridProps,
    ...restProps,
  };

  const api: ResourceListApi = useMemo(() => {
    const incrementRefreshIndex = () => setRefreshIndex((prev) => prev + 1);
    const hasAppliedFilter = (filterName: string) => {
      const filterModel = gridUrlState[ResourceListSearchParams.FilterModel];
      return !!filterModel?.[filterName];
    };
    const getAppliedFilter = (filterName: string) => {
      const applied = gridUrlState[ResourceListSearchParams.FilterModel]?.[
        filterName
      ] as { v: any; o: string } | undefined;

      return applied ? { value: applied.v, operator: applied.o } : undefined;
    };
    const removeAppliedFilter = (filterName: string) => {
      const filterModel = gridUrlState[ResourceListSearchParams.FilterModel];
      delete filterModel?.[filterName];
      setGridUrlState({
        [ResourceListSearchParams.FilterModel]: filterModel,
      });
    };
    const addAppliedFilter = (
      filterName: string,
      params: {
        value: any;
        operator: string;
      },
    ) => {
      const filterModel = gridUrlState[ResourceListSearchParams.FilterModel];
      setGridUrlState({
        [ResourceListSearchParams.FilterModel]: {
          ...filterModel,
          [filterName]: {
            v: params.value,
            o: params.operator,
          },
        },
      });
    };
    return {
      incrementRefreshIndex,
      hasAppliedFilter,
      getAppliedFilter,
      removeAppliedFilter,
      addAppliedFilter,
    };
  }, [setRefreshIndex, gridUrlState, setGridUrlState]);

  return {
    dataGridProps,
    context: { api },
    queryResult,
    key: resource,
  };
};
